export const ARTIST_PRODUCTION_SUPABASE_ORIGIN =
	"https://crlauxhyezjqwdfetukb.supabase.co";

export type ArtistDeploymentRegion = "china" | "global";
export type ArtistCommunityShareMode = "china" | "international";
export type ArtistAuthProviderKey =
	| "apple"
	| "douyin"
	| "facebook"
	| "google"
	| "linux-do"
	| "qq"
	| "watcha"
	| "wechat"
	| "x";

export interface ArtistBackendEnvironment {
	readonly VITE_ARTIST_REGION?: ArtistDeploymentRegion | undefined;
	readonly VITE_SUPABASE_URL: string | undefined;
	readonly VITE_SUPABASE_PUBLISHABLE_KEY: string | undefined;
}

export type AuthProviderConfigurationStatus =
	| "missing"
	| "invalid-origin"
	| "available";

export interface ArtistRuntimeConfiguration {
	readonly region: ArtistDeploymentRegion;
	readonly supabaseOrigin: string;
	readonly supabasePublishableKey: string;
	readonly providerConfiguration: AuthProviderConfigurationStatus;
	readonly credentialAuthenticationEnabled: boolean;
	readonly authenticationProviderKeys: readonly ArtistAuthProviderKey[];
	readonly connectedAccountProviderKeys: readonly ArtistAuthProviderKey[];
	readonly communityShareMode: ArtistCommunityShareMode;
	readonly publicSiteOrigin: string;
}

export interface ArtistPublicAssetLocation {
	readonly bucket: string;
	readonly object_path: string;
}

const REGIONAL_POLICY: Readonly<
	Record<
		ArtistDeploymentRegion,
		Pick<
			ArtistRuntimeConfiguration,
			| "authenticationProviderKeys"
			| "credentialAuthenticationEnabled"
			| "connectedAccountProviderKeys"
			| "communityShareMode"
		>
	>
> = {
	china: {
		credentialAuthenticationEnabled: false,
		authenticationProviderKeys: ["qq", "watcha", "linux-do"],
		connectedAccountProviderKeys: ["qq", "watcha", "linux-do"],
		communityShareMode: "china",
	},
	global: {
		credentialAuthenticationEnabled: true,
		authenticationProviderKeys: ["google", "facebook", "x", "apple"],
		connectedAccountProviderKeys: ["google", "facebook", "x", "apple"],
		communityShareMode: "international",
	},
};

export function resolveAuthProviderConfiguration(
	environment: ArtistBackendEnvironment,
): AuthProviderConfigurationStatus {
	const rawUrl = environment.VITE_SUPABASE_URL?.trim();
	const publishableKey = environment.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();
	if (!rawUrl || !publishableKey) return "missing";
	try {
		const url = new URL(rawUrl);
		const region = environment.VITE_ARTIST_REGION ?? "global";
		const isRootHttpsOrigin =
			url.protocol === "https:" &&
			url.username === "" &&
			url.password === "" &&
			url.port === "" &&
			url.pathname === "/" &&
			url.search === "" &&
			url.hash === "";
		const isExpectedRegion =
			region === "global"
				? url.origin === ARTIST_PRODUCTION_SUPABASE_ORIGIN
				: url.origin !== ARTIST_PRODUCTION_SUPABASE_ORIGIN;
		if (!isRootHttpsOrigin || !isExpectedRegion) {
			return "invalid-origin";
		}
	} catch {
		return "invalid-origin";
	}
	return "available";
}

export function browserAuthProviderConfiguration(): AuthProviderConfigurationStatus {
	return resolveAuthProviderConfiguration({
		VITE_ARTIST_REGION: import.meta.env.VITE_ARTIST_REGION,
		VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
		VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env
			.VITE_SUPABASE_PUBLISHABLE_KEY,
	});
}

export function browserArtistRuntimeConfiguration(): ArtistRuntimeConfiguration {
	return resolveArtistRuntimeConfiguration({
		VITE_ARTIST_REGION: import.meta.env.VITE_ARTIST_REGION,
		VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
		VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env
			.VITE_SUPABASE_PUBLISHABLE_KEY,
	});
}

export function resolveArtistRuntimeConfiguration(
	environment: ArtistBackendEnvironment,
): ArtistRuntimeConfiguration {
	const region = environment.VITE_ARTIST_REGION ?? "global";
	const rawUrl = environment.VITE_SUPABASE_URL?.trim();
	const publishableKey =
		environment.VITE_SUPABASE_PUBLISHABLE_KEY?.trim() ?? "";
	const providerConfiguration = resolveAuthProviderConfiguration(environment);
	const supabaseOrigin = rawUrl
		? new URL(rawUrl).origin
		: ARTIST_PRODUCTION_SUPABASE_ORIGIN;
	return {
		region,
		supabaseOrigin,
		supabasePublishableKey: publishableKey,
		providerConfiguration,
		publicSiteOrigin: region === "china" ? "https://artistapp.cn" : "https://artistapp.net",
		...REGIONAL_POLICY[region],
	};
}

export function artistPublicTrackURL(
	configuration: Pick<ArtistRuntimeConfiguration, "publicSiteOrigin">,
	trackId: string,
): string {
	const normalized = trackId.trim().replace(/^public:/u, "");
	if (normalized.length === 0) throw new Error("Track ID is required");
	return `${configuration.publicSiteOrigin}/track/${encodeURIComponent(normalized)}`;
}

export function artistPublicAssetURL(
	configuration: Pick<
		ArtistRuntimeConfiguration,
		"publicSiteOrigin" | "region" | "supabaseOrigin"
	>,
	asset: ArtistPublicAssetLocation,
): string {
	const path = asset.object_path.split("/").map(encodeURIComponent).join("/");
	if (configuration.region === "global") {
		return `${configuration.publicSiteOrigin}/${path}`;
	}
	return `${configuration.supabaseOrigin}/storage/v1/object/public/${encodeURIComponent(asset.bucket)}/${path}`;
}

export function artistBackendOrigin(
	environment: ArtistBackendEnvironment,
): string {
	const status = resolveAuthProviderConfiguration(environment);
	if (status !== "available") {
		throw new Error(`Supabase browser configuration is ${status}`);
	}
	return new URL(environment.VITE_SUPABASE_URL as string).origin;
}

export function browserArtistBackendOrigin(): string {
	const region = import.meta.env.VITE_ARTIST_REGION ?? "global";
	const rawUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
	if (!rawUrl && region === "global") {
		return ARTIST_PRODUCTION_SUPABASE_ORIGIN;
	}
	const environment: ArtistBackendEnvironment = {
		VITE_ARTIST_REGION: region,
		VITE_SUPABASE_URL: rawUrl,
		VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env
			.VITE_SUPABASE_PUBLISHABLE_KEY,
	};
	return artistBackendOrigin(environment);
}
